/**
 * useCreateBusiness — React Query mutation for adding a new business.
 *
 * Returns: useMutation that calls POST /businesses with
 * { name, slug?, currency?, industry?, description? }.
 *
 * Authority: tenant_owner. Plan-tier limit enforced server-side.
 *
 * On success: invalidates the businesses caches so the Sidebar dropdown,
 * portfolio overview, and group views all refresh.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/services/api";

export interface CreateBusinessInput {
  name: string;
  slug?: string;
  currency?: string;
  industry?: string;
  description?: string;
}

export interface CreatedBusiness {
  _id: string;
  name: string;
  slug: string;
  currency?: string;
  industry?: string;
  isDefault: boolean;
  createdAt: string;
}

export function useCreateBusiness() {
  const qc = useQueryClient();
  return useMutation<CreatedBusiness, unknown, CreateBusinessInput>({
    mutationFn: async (input) => {
      const res = await api.post<{
        success: boolean;
        data: CreatedBusiness;
      }>("/businesses", input);
      const body = res.data;
      return (
        (body as { data?: CreatedBusiness })?.data ||
        (body as unknown as CreatedBusiness)
      );
    },
    onSuccess: () => {
      // Invalidate every cached query that lists or counts businesses
      qc.invalidateQueries({ queryKey: ["businesses-mine"] });
      qc.invalidateQueries({ queryKey: ["businesses-mine-for-active"] });
      qc.invalidateQueries({ queryKey: ["my-businesses"] });
      qc.invalidateQueries({ queryKey: ["portfolio-today"] });
      qc.invalidateQueries({ queryKey: ["group-snapshot"] });
    },
  });
}

export default useCreateBusiness;
